import FluentOpen20Filled from '~icons/fluent/open-20-filled'
import FluentShare20Regular from '~icons/fluent/share-20-regular'
import IcOutlineSms from '~icons/ic/outline-sms'
import Text from 'src/components/ui/Text'
import type { TackCuratedArtworkItem } from 'src/services/orpc/tackCuratedArtworks'
import type { OrpcArtworkPreviewFragment } from 'src/services/orpc/utils/typeAdapters'
import { toArtworkPreviewFragment } from 'src/services/orpc/utils/typeAdapters'
import { getArtworkLink, isAnimatedArtwork } from 'src/utils/artwork'
import { track } from 'src/utils/tracker'
import { openShareArtworkDialog } from '../ArtworkDetail/ArtworkShare/ArtworkShareDialog'
import { CardCornerOverlay } from '../CardCornerOverlay'
import ArtworkLikeButton from './ArtworkLikeButton'
import ArtworkTypeLabel from './ArtworkTypeLabel'
import BaseArtworkItemPreviewCard from './BaseArtworkItemPreviewCard'

export function CuratedArtworkCard({
  item,
  onOpen,
}: {
  item: TackCuratedArtworkItem
  onOpen?: (artwork: OrpcArtworkPreviewFragment) => void
}) {
  const isPublic = item.visibility === 'PUBLIC'
  const artwork = toArtworkPreviewFragment(item)
  const isAnimated = isAnimatedArtwork(artwork)
  const artworkUrl = getArtworkLink(artwork)
  const artworkTypeLabel = isAnimated ? ('ANIMATED' as const) : undefined

  return (
    <BaseArtworkItemPreviewCard
      mediaId={item.mediaId}
      label={item.title ?? item.prompts}
      secondLabel={item.author?.displayName ?? item.authorName}
      time={item.createdAt}
      to={artworkUrl}
      onClick={
        onOpen
          ? e => {
              e.preventDefault()
              onOpen(artwork)
            }
          : undefined
      }
      showAuthorAvatar={artwork.author != null}
      animated={isAnimated}
      border={'author'}
      artwork={artwork}
      showLikeBtn
      likeBtn={
        <ArtworkLikeButton
          artworkId={item.id}
          initialCount={item.likedCount}
          initialValue={item.liked}
          hideValueOnZero
        />
      }
      menu={[
        isPublic && {
          id: 'share',
          icon: <FluentShare20Regular className="size-5" />,
          children: <Text i18nKey="common:share.label" />,
          onAction: () => {
            track('share_my_work_click', { scene: 'curated' })
            openShareArtworkDialog({
              id: item.id,
              title: item.title ?? '',
            })
          },
        },
        {
          id: 'open-in-new-window',
          icon: <FluentOpen20Filled className="size-5" />,
          children: <Text i18nKey="action:open-in-new-window" />,
          onAction: () =>
            window.open(artworkUrl, '_blank', 'noopener,noreferrer'),
        },
      ]}
      mediaExtra={
        <>
          <CardCornerOverlay
            corner="top-left"
            className="bg-transparent shadow-none backdrop-filter-none"
          >
            {artworkTypeLabel != null && (
              <ArtworkTypeLabel
                type={artworkTypeLabel}
                className="absolute top-1 left-1 ring-1 ring-black/20"
              />
            )}
          </CardCornerOverlay>
          {item.commentCount > 0 && (
            <CardCornerOverlay corner="bottom-left">
              <div className="flex items-center gap-1">
                <IcOutlineSms className="text-white/70 text-xl" />
                <span className="text-white/90">{item.commentCount}</span>
              </div>
            </CardCornerOverlay>
          )}
        </>
      }
    />
  )
}
